import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ImagePlus, Loader2, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { compressImage, MAX_PHOTO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo, Footer } from "@/components/Layout";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen flex flex-col">
    <header className="bg-[#13201A] h-16 flex items-center px-4 sm:px-10"><Link to="/"><Logo /></Link></header>
    <main className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="card p-6 sm:p-10 max-w-[520px] w-full fade-up" data-testid={testId}>{children}</div>
    </main>
    <Footer />
  </div>
);

async function uploadLogo(file) {
  const blob = await compressImage(file, 800);
  if (blob.size > MAX_PHOTO) throw new Error("Le logo dépasse 5 Mo.");
  const form = new FormData();
  form.append("file", blob, "logo.jpg");
  const { data } = await api.post("/upload", form);
  return data.path;
}

function CreateForm({ info }) {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [f, setF] = useState({ club_name: info.club_name || "", email: info.email || "", password: "", season: "2026-2027" });
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/club-creation", { ...f, token: info.token });
      let club = data.club;
      if (logo) {
        try {
          const path = await uploadLogo(logo);
          club = (await api.put("/club", { name: club.name, logo_path: path, season: club.season, manager_name: "", manager_phone: "", manager_email: club.manager_email })).data;
        } catch (err) { toast.error("Club créé, mais le logo n'a pas pu être ajouté. Ajoutez-le dans les paramètres."); }
      }
      setSession({ ...data, club });
      nav("/?bienvenue=1");
    } catch (err) { setError(apiError(err)); setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-6" data-testid="create-club-form">
      <label className="block"><span className="field-label">Nom du club</span><input className="field" required value={f.club_name} onChange={set("club_name")} data-testid="create-club-name-input" /></label>
      <label className="block"><span className="field-label">Email</span><input className="field" type="email" required autoComplete="email" value={f.email} onChange={set("email")} data-testid="create-club-email-input" /></label>
      <label className="block"><span className="field-label">Mot de passe (8 caractères minimum)</span><input className="field" type="password" required minLength={8} autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="create-club-password-input" /></label>
      <label className="block"><span className="field-label">Saison en cours</span><input className="field" required value={f.season} onChange={set("season")} data-testid="create-club-season-input" /></label>
      <div>
        <span className="field-label">Logo du club <span className="font-normal text-[#5B665F]">(facultatif)</span></span>
        <button type="button" className="btn-outline btn-sm" onClick={() => fileRef.current.click()} data-testid="create-club-logo-button"><ImagePlus size={15} /> {logo ? logo.name : "Choisir un logo"}</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setLogo(e.target.files?.[0] || null)} data-testid="create-club-logo-input" />
      </div>
      {error && <p className="text-sm text-[#8A4B00] bg-[#FDEBD3] rounded-lg px-3 py-2" data-testid="create-club-error">{error}</p>}
      <button type="submit" className="btn-primary w-full h-12" disabled={busy} data-testid="create-club-submit">{busy ? <><Loader2 size={16} className="animate-spin" /> Création…</> : "Créer mon club"}</button>
    </form>
  );
}

export default function CreateClub() {
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/club-creation/check", { params: { session_id: params.get("session_id") || "", token: params.get("token") || "" } })
      .then((r) => setInfo(r.data)).catch(() => setInfo({ state: "invalid" }));
  }, [params]);

  if (!info) return <Shell testId="create-club-loading"><p className="text-[#5B665F] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Vérification du paiement…</p></Shell>;
  if (info.state === "used") return (
    <Shell testId="create-club-used">
      <CheckCircle2 size={34} className="text-[#1E6B45]" />
      <h1 className="text-3xl font-bold tracking-tight mt-4">Votre club est déjà créé</h1>
      <p className="text-[15px] text-[#5B665F] mt-3">Connectez-vous avec l'email et le mot de passe choisis lors de la création.</p>
      <Link to="/connexion" className="btn-primary mt-6" data-testid="create-club-login-link">Se connecter</Link>
    </Shell>
  );
  if (info.state !== "ok") return (
    <Shell testId="create-club-invalid">
      <XCircle size={34} className="text-[#8A4B00]" />
      <h1 className="text-3xl font-bold tracking-tight mt-4">Paiement introuvable, contactez-nous</h1>
      {info.contact_email && <p className="text-[15px] text-[#5B665F] mt-3">Écrivez-nous à <a href={`mailto:${encodeURIComponent(info.contact_email)}`} className="link" data-testid="create-club-contact">{info.contact_email}</a>, nous vous répondons rapidement.</p>}
    </Shell>
  );
  return (
    <Shell testId="create-club-page">
      <h1 className="text-3xl font-bold tracking-tight">Créez votre club</h1>
      <p className="text-[15px] text-[#5B665F] mt-2">Paiement confirmé, merci ! Il ne reste qu'à créer votre espace.</p>
      <CreateForm info={info} />
    </Shell>
  );
}
